import { useState } from "react";
import { Table, TableBody, TableRow, TableCell } from "../../../design/table";
import { Input } from "../../../design/input";
import { Button } from "../../../design/button";
import { Text } from "../../../design/text";
import { ZillowProperty, ZillowService } from "../../../services/ZillowService";
import { on } from "events";
import {
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../../design/dialog";
import { ErrorMessage, Field } from "../../../design/fieldset";
import EmptyStateView from "../../../components/EmptyStateView";
import LoadingView from "../../../components/LoadingView";

interface Props {
  onPropertySelected: (property: ZillowProperty | null) => void;
  onClose?: () => void;
}

const ChooseZillowPropertyStep: React.FC<Props> = ({
  onPropertySelected,
  onClose,
}) => {
  const [location, setLocation] = useState("");
  const [properties, setProperties] = useState<ZillowProperty[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isGatheringInfo, setIsGatheringInfo] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    setError("");
    try {
      const propertyResults = await ZillowService.search(location);
      setProperties(propertyResults);
    } catch (error) {
      console.error("Error searching for properties:", error);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handlePropertySelected = async (property: ZillowProperty) => {
    setError("");
    try {
      setIsGatheringInfo(true);
      const zestimate = await ZillowService.zestimate(property.zpid);
      onPropertySelected({
        ...property,
        amount: zestimate.value,
      });
    } catch (error) {
      console.error("Error searching for properties:", error);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsGatheringInfo(false);
    }
  };

  return (
    <>
      <DialogTitle>Search Property</DialogTitle>
      <DialogDescription>
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <Input
            placeholder="Enter address, city, or ZIP"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
          <Button type="submit" disabled={isSearching || !location}>
            {isSearching ? "Searching..." : "Search"}
          </Button>
          {error && (
            <ErrorMessage className="text-red-500">{error}</ErrorMessage>
          )}
        </form>
      </DialogDescription>
      <DialogBody>
        {properties.length > 0 && !isSearching && !isGatheringInfo && (
          <Table>
            <TableBody>
              {properties.map((property) => (
                <TableRow
                  key={property.zpid}
                  onClick={() => handlePropertySelected(property)}
                  className="cursor-pointer hover:bg-gray-100"
                >
                  <TableCell className="font-medium">
                    {property.address}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {isSearching && <LoadingView title="Searching for Properties..." />}
        {isGatheringInfo && <LoadingView title="Gathering Property Info..." />}
        {properties.length === 0 && !isSearching && !isGatheringInfo && (
          <EmptyStateView
            title="No Properties Found"
            message="Search for properties by address, city, or ZIP code."
          />
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={isSearching}>
          Back
        </Button>
      </DialogActions>
    </>
  );
};

export default ChooseZillowPropertyStep;
